import { useState } from 'react';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';
import { omikujiData } from '../../mocks/omikuji';

interface OmikujiResult {
  flower: string;
  flowerImage: string;
  meaning: string;
  message: string;
  luck: string;
  color: string;
  advice: string;
}

export default function Omikuji() {
  const [result, setResult] = useState<OmikujiResult | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const drawOmikuji = () => {
    setIsDrawing(true);
    setHasDrawn(true);
    setImageLoaded(false);
    setImageError(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // ランダムに結果を選択
    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * omikujiData.length);
      setResult(omikujiData[randomIndex]);
      setIsDrawing(false);
    }, 2000);
  };

  const resetOmikuji = () => {
    setResult(null);
    setHasDrawn(false);
    setIsDrawing(false);
    setImageLoaded(false);
    setImageError(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            花のおみくじ
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">
            今日のあなたにぴったりの花を占いで見つけてみませんか？
            <br />
            花からのメッセージがあなたを導いてくれるでしょう
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* おみくじを引く前 */}
            {!hasDrawn && (
              <div className="text-center">
                <div className="bg-white rounded-3xl shadow-xl p-12 mb-8">
                  <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full flex items-center justify-center">
                    <i className="ri-magic-line text-6xl text-purple-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">
                    今日の運勢を花で占いましょう
                  </h2>
                  <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                    心を静めて、下のボタンを押してください。<br />
                    あなたにぴったりの花が現れて、素敵なメッセージを届けてくれます。
                  </p>
                  <button
                    onClick={drawOmikuji}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-12 py-4 rounded-full text-xl font-bold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
                  >
                    <i className="ri-magic-line mr-2"></i>
                    おみくじを引く
                  </button>
                </div>
              </div>
            )}

            {/* おみくじを引いている最中 */}
            {isDrawing && (
              <div className="text-center">
                <div className="bg-white rounded-3xl shadow-xl p-12">
                  <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full flex items-center justify-center animate-spin">
                    <i className="ri-magic-line text-6xl text-purple-600"></i>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    占い中...
                  </h2>
                  <p className="text-gray-600 text-lg">
                    あなたにぴったりの花を探しています
                  </p>
                </div>
              </div>
            )}

            {/* 結果表示 */}
            {result && !isDrawing && (
              <div className="text-center">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                  {/* 結果ヘッダー */}
                  <div className={`${result.color} py-8 px-6`}>
                    <h2 className="text-3xl font-bold text-white mb-2">
                      今日のあなたの花
                    </h2>
                    <div className="text-6xl mb-4">🌸</div>
                    <h3 className="text-4xl font-bold text-white">
                      {result.flower}
                    </h3>
                  </div>

                  {/* 花の画像 */}
                  <div className="p-8">
                    <div className="w-64 h-64 mx-auto mb-8 rounded-full overflow-hidden shadow-lg bg-gray-100 relative">
                      {!imageLoaded && !imageError && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent"></div>
                        </div>
                      )}
                      {imageError && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                          <div className="text-center">
                            <i className="ri-flower-line text-6xl text-purple-400 mb-2"></i>
                            <p className="text-gray-600 text-sm">{result.flower}</p>
                          </div>
                        </div>
                      )}
                      <img 
                        src={result.flowerImage}
                        alt={result.flower}
                        className={`w-full h-full object-cover object-top transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                      />
                    </div>

                    {/* 運勢 */}
                    <div className="mb-8">
                      <div className={`inline-block px-6 py-2 rounded-full text-white font-bold text-xl ${result.color}`}>
                        {result.luck}
                      </div>
                    </div>

                    {/* 花言葉 */}
                    <div className="mb-8 p-6 bg-gray-50 rounded-2xl">
                      <h4 className="text-xl font-bold text-gray-800 mb-3">花言葉</h4>
                      <p className="text-lg text-gray-700 font-medium">
                        「{result.meaning}」
                      </p>
                    </div>

                    {/* メッセージ */}
                    <div className="mb-8 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl">
                      <h4 className="text-xl font-bold text-gray-800 mb-3">モモちゃんからのメッセージ</h4>
                      <p className="text-gray-700 leading-relaxed">
                        {result.message}
                      </p>
                    </div>

                    {/* アドバイス */}
                    <div className="mb-8 p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl">
                      <h4 className="text-xl font-bold text-gray-800 mb-3">モモちゃんからのアドバイス</h4>
                      <p className="text-gray-700 leading-relaxed">
                        {result.advice}
                      </p>
                    </div>

                    {/* もう一度引くボタン */}
                    <button
                      onClick={resetOmikuji}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      もう一度引く
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 説明セクション */}
            <div className="mt-16 grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">花言葉の力</h3>
                <p className="text-gray-600">
                  古くから伝わる花言葉には、深い意味とメッセージが込められています
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-line text-2xl text-pink-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">運勢占い</h3>
                <p className="text-gray-600">
                  花の持つエネルギーがあなたの今日の運勢を教えてくれます
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lightbulb-line text-2xl text-indigo-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">毎日の指針</h3>
                <p className="text-gray-600">
                  花からのアドバイスで、素敵な一日を過ごすヒントを見つけましょう
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}